import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import QuickPostForm from './QuickPostForm';
import DetailedJobForm from './DetailedJobForm';
import { 
  Plus, 
  MapPin, 
  DollarSign, 
  Clock, 
  AlertTriangle,
  CheckCircle,
  ArrowLeft,
  Send,
  RotateCcw,
  Edit
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobBoard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStep, setCurrentStep] = useState('list'); // 'list', 'quick-post', 'detailed-form'
  const [selectedJob, setSelectedJob] = useState(null);
  const [showPassBackModal, setShowPassBackModal] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [passBackReason, setPassBackReason] = useState('');

  useEffect(() => {
    fetchJobs();
    
    // Check if we should auto-open the create form
    if (searchParams.get('create') === 'true') {
      setCurrentStep('quick-post');
      // Clean up the URL parameter
      navigate('/jobs', { replace: true });
    }
  }, [searchParams, navigate]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/jobs`);
      setJobs(response.data);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickPostSuccess = (createdJob) => {
    // Move to detailed form with the created job
    setSelectedJob(createdJob);
    setCurrentStep('detailed-form');
    fetchJobs(); // Refresh jobs list
  };

  const handleDetailedFormSuccess = () => {
    // Navigate to job detail with funding modal auto-open
    if (selectedJob?.id) {
      navigate(`/jobs/${selectedJob.id}?open=fund`);
    } else {
      // Fallback to jobs list
      setCurrentStep('list');
      setSelectedJob(null);
      fetchJobs();
    }
  };

  const handleCancel = () => {
    // Return to jobs list
    setCurrentStep('list');
    setSelectedJob(null);
  };

  const openDetailedForm = (job) => {
    setSelectedJob(job);
    setCurrentStep('detailed-form');
  };

  const handleAcceptJob = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/accept`, { job_id: jobId });
      toast.success('Job accepted successfully!');
      fetchJobs();
    } catch (error) {
      console.error('Failed to accept job:', error);
      const message = error.response?.data?.detail || 'Failed to accept job';
      toast.error(message);
    }
  };

  const handleCompleteJob = async (jobId) => {
    try {
      const response = await axios.post(`${API}/jobs/complete`, { job_id: jobId });
      toast.success('Job completed! Commission calculated.');
      fetchJobs();
    } catch (error) {
      console.error('Failed to complete job:', error);
      toast.error('Failed to complete job');
    }
  };

  const handlePassBackJob = async (jobId, reason) => {
    try {
      await axios.post(`${API}/jobs/pass-back`, { 
        job_id: jobId, 
        reason: reason || 'Unable to complete job' 
      });
      toast.success('Job passed back successfully!');
      setShowPassBackModal(false);
      setPassBackReason('');
      setSelectedJobId(null);
      fetchJobs();
    } catch (error) {
      console.error('Failed to pass back job:', error);
      toast.error('Failed to pass back job');
    }
  };

  const openPassBackModal = (jobId) => {
    setSelectedJobId(jobId);
    setShowPassBackModal(true);
  };

  const handlePassBackSubmit = () => {
    if (passBackReason.trim()) {
      handlePassBackJob(selectedJobId, passBackReason.trim());
    } else {
      toast.error('Please provide a reason for passing back the job');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      posted: 'status-posted',
      accepted: 'status-accepted',
      completed: 'status-completed',
      passed_back: 'status-passed_back'
    };
    return badges[status] || 'status-posted';
  };

  const getUrgencyBadge = (urgency) => {
    const badges = {
      // Current values from QuickPostForm
      normal: 'urgency-low',
      asap: 'urgency-high',
      after_hours: 'urgency-emergency',
      // Legacy values (backward compatibility)
      low: 'urgency-low',
      medium: 'urgency-medium',
      high: 'urgency-high',
      emergency: 'urgency-emergency'
    };
    return badges[urgency] || 'urgency-medium';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <button 
                onClick={() => navigate('/dashboard')} 
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-xl font-bold text-gray-900">
                {currentStep === 'quick-post' ? 'Quick Post Job' :
                 currentStep === 'detailed-form' ? 'Add Job Details' : 'Job Board'}
              </h1>
            </div>
            
            {currentStep === 'list' && (user.can_post_jobs || user.is_admin) && (
              <button
                onClick={() => setCurrentStep('quick-post')}
                className="jop-button"
              >
                <Plus className="h-5 w-5 mr-2" />
                Post Job
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Render based on current step */}
        {currentStep === 'quick-post' && (
          <QuickPostForm 
            onSuccess={handleQuickPostSuccess}
            onCancel={handleCancel}
          />
        )}

        {currentStep === 'detailed-form' && selectedJob && (
          <DetailedJobForm 
            job={selectedJob}
            onSuccess={handleDetailedFormSuccess}
            onCancel={handleCancel}
          />
        )}

        {currentStep === 'list' && (
          <>
            {/* Progress Banner for jobs missing details */}
            {jobs.some(job => job.originator_id === user.id && !job.detailed_info_added && job.status === 'posted') && (
              <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-medium text-amber-800">
                      Basic details posted. Add more info to help receivers deliver.
                    </h3>
                    <p className="text-xs text-amber-700 mt-1">
                      Click "Add Details" on any of your posted jobs to provide client info, precise location, and pricing breakdown.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Jobs List */}
            <div className="space-y-6">
              {jobs.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs available</h3>
                  <p className="text-gray-600">
                    {user.can_post_jobs ? 
                      'Post your first overflow job to get started.' :
                      user.can_receive_jobs ? 
                      'Check back later for new job opportunities.' :
                      'You don\'t have permission to view jobs.'
                    }
                  </p>
                </div>
              ) : (
                jobs.map((job) => (
                  <div key={job.id} className="jop-card p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {job.title}
                          </h3>
                          <div className="flex gap-1">
                            {job.industry.map((ind) => (
                              <span key={ind} className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded-full">
                                {ind}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-1" />
                            {job.location}
                          </div>
                          <div className="flex items-center">
                            <DollarSign className="h-4 w-4 mr-1" />
                            ${job.quoted_price}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex space-x-2">
                        <span className={`status-badge ${getStatusBadge(job.status)}`}>
                          {job.status.replace('_', ' ')}
                        </span>
                        <span className={`status-badge ${getUrgencyBadge(job.urgency_level)}`}>
                          {job.urgency_level.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <p className="text-sm text-gray-700">
                        <strong>Specification:</strong> {job.job_specification || job.tyre_specification || 'No specification provided'}
                      </p>
                      <p className="text-sm text-gray-700 mt-1">
                        <strong>Posted by:</strong> {job.originator_business}
                      </p>
                      {job.accepted_by_business && (
                        <p className="text-sm text-gray-700 mt-1">
                          <strong>Accepted by:</strong> {job.accepted_by_business}
                        </p>
                      )}
                      {job.pass_back_reason && (
                        <p className="text-sm text-red-700 mt-1">
                          <strong>Pass-back reason:</strong> {job.pass_back_reason}
                        </p>
                      )}
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex space-x-3">
                      {user.can_receive_jobs && job.status === 'posted' && job.originator_id !== user.id && (
                        <button
                          onClick={() => handleAcceptJob(job.id)}
                          className="jop-button"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Accept Job
                        </button>
                      )}
                      
                      {job.accepted_by === user.id && job.status === 'accepted' && (
                        <>
                          <button
                            onClick={() => handleCompleteJob(job.id)}
                            className="jop-button"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Mark Complete
                          </button>
                          <button
                            onClick={() => openPassBackModal(job.id)}
                            className="jop-button-secondary"
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Pass Back
                          </button>
                        </>
                      )}
                      
                      {job.status === 'passed_back' && user.can_receive_jobs && job.accepted_by !== user.id && job.originator_id !== user.id && (
                        <button
                          onClick={() => handleAcceptJob(job.id)}
                          className="jop-button"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Accept Job
                        </button>
                      )}

                      {/* Add Details button for originators */}
                      {job.originator_id === user.id && !job.detailed_info_added && (job.status === 'posted' || job.status === 'passed_back') && (
                        <button
                          onClick={() => openDetailedForm(job)}
                          className="jop-button-secondary"
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Add Details
                        </button>
                      )}
                    </div>
                    
                    <div className="mt-3 text-xs text-gray-500">
                      Posted: {new Date(job.created_at).toLocaleString()}
                      {job.accepted_at && (
                        <> • Accepted: {new Date(job.accepted_at).toLocaleString()}</>
                      )}
                      {job.completed_at && (
                        <> • Completed: {new Date(job.completed_at).toLocaleString()}</>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Pass Back Modal */}
      {showPassBackModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="jop-card p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Pass Back Job</h3>
            <p className="text-sm text-gray-600 mb-4">
              Please provide a reason for passing back this job. It will be re-sent to all available receivers.
            </p>
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason for passing back
              </label>
              <textarea
                value={passBackReason}
                onChange={(e) => setPassBackReason(e.target.value)}
                className="jop-input resize-none"
                rows="3"
                placeholder="e.g., No stock available, truck breakdown, etc."
                required
              />
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handlePassBackSubmit}
                className="jop-button flex-1"
              >
                Pass Back Job
              </button>
              <button
                onClick={() => {
                  setShowPassBackModal(false);
                  setPassBackReason('');
                  setSelectedJobId(null);
                }}
                className="jop-button-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobBoard;
